import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { APIError, api, type User } from './api';

type AuthState =
  | { status: 'loading' }
  | { status: 'authed'; user: User }
  | { status: 'anonymous' };

type AuthContextValue = {
  state: AuthState;
  refresh: () => Promise<void>;
  setAuthed: (user: User) => void;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const user = await api.me();
      setState({ status: 'authed', user });
    } catch (err) {
      // If something else has already authed us in the meantime (e.g. /auth/verify
      // completing in parallel), don't downgrade.
      setState((prev) => (prev.status === 'authed' ? prev : { status: 'anonymous' }));
      if (!(err instanceof APIError && err.status === 401)) {
        console.warn('[auth] refresh failed', err);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setAuthed = useCallback((user: User) => {
    setState({ status: 'authed', user });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // ignore — we'll fall to anonymous below
    }
    setState({ status: 'anonymous' });
  }, []);

  return <AuthCtx.Provider value={{ state, refresh, setAuthed, signOut }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'loading') {
    return (
      <div className="min-h-full flex items-center justify-center text-sm text-fg-muted">
        Checking session…
      </div>
    );
  }
  if (state.status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
